import { CONTACT_PHONES } from './contactPhones';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.promocionalesenstock.com';

export const generateWebsiteSchema = () => ({
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'Promocionales en Stock',
  description: 'Productos promocionales personalizados en Colombia. Merchandising corporativo, regalos empresariales y artículos publicitarios.',
  publisher: {
    '@id': `${SITE_URL}/#organization`
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/buscar?q={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  },
  inLanguage: 'es-CO'
});

export const generateOrganizationSchema = () => ({
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'Promocionales en Stock',
  alternateName: 'En Stock Promocionales',
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    '@id': `${SITE_URL}/#logo`,
    url: `${SITE_URL}/assets/images/logo.svg`,
    contentUrl: `${SITE_URL}/assets/images/logo.svg`,
    width: 250,
    height: 60,
    caption: 'Promocionales en Stock'
  },
  image: {
    '@id': `${SITE_URL}/#logo`
  },
  contactPoint: [{
    '@type': 'ContactPoint',
    telephone: CONTACT_PHONES.join(' - '),
    contactType: 'sales',
    contactOption: 'TollFree',
    areaServed: 'CO',
    availableLanguage: ['Spanish', 'es']
  }],
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Cl. 75 #13-51, Chapinero, Bogotá',
    addressLocality: 'Bogotá',
    addressRegion: 'Bogotá D.C.',
    postalCode: '110231',
    addressCountry: 'CO'
  },
  sameAs: [
    'https://www.facebook.com/promocionalesenstock',
    'https://www.instagram.com/promocionalesenstock',
    'https://www.linkedin.com/company/promocionalesenstock',
    'https://twitter.com/promocionalesenstock'
  ],
  openingHoursSpecification: [{
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '08:00',
    closes: '18:00'
  }]
});

export const generateBreadcrumbSchema = (items) => ({
  '@type': 'BreadcrumbList',
  '@id': `${SITE_URL}${items[items.length - 1].url}#breadcrumb`,
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`
  }))
});

export const generateCollectionPageSchema = ({
  title,
  description,
  url,
  breadcrumbs,
  currentPage = 1,
  totalPages = 1
}) => ({
  '@context': 'https://schema.org',
  '@graph': [
    generateWebsiteSchema(),
    generateOrganizationSchema(),
    generateBreadcrumbSchema(breadcrumbs),
    {
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}${url}`,
      url: `${SITE_URL}${url}`,
      name: title,
      description: description,
      isPartOf: {
        '@id': `${SITE_URL}/#website`
      },
      breadcrumb: {
        '@id': `${SITE_URL}${url}#breadcrumb`
      },
      inLanguage: 'es-CO',
      potentialAction: [{
        '@type': 'ReadAction',
        target: [`${SITE_URL}${url}`]
      }]
    }
  ]
});

export const generateProductListSchema = ({
  products,
  currentPage,
  totalCount,
  pageSize = 12,
  pageUrl
}) => ({
  '@type': 'ItemList',
  '@id': `${SITE_URL}${pageUrl}#product-list`,
  name: 'Catálogo de Productos Promocionales',
  description: `Lista de productos promocionales - Página ${currentPage} de ${Math.ceil(totalCount / pageSize)}`,
  numberOfItems: products.length,
  itemListElement: products.map((product, index) => ({
    '@type': 'ListItem',
    position: index + 1 + (currentPage - 1) * pageSize,
    item: generateProductSchema(product)
  }))
});

export const generateProductSchema = (product) => ({
  '@type': 'Product',
  '@id': `${SITE_URL}/producto/${product.slug || product.id}`,
  url: `${SITE_URL}/producto/${product.slug || product.id}`,
  name: product.name,
  description: product.description || `${product.name} - Producto promocional personalizable en Colombia`,
  image: product.images?.map(img => `${SITE_URL}${img}`) || [`${SITE_URL}/images/producto-default.jpg`],
  sku: product.sku || `SKU-${product.id}`,
  mpn: product.mpn || product.sku || `MPN-${product.id}`,
  brand: {
    '@type': 'Brand',
    name: product.brand || 'Promocionales en Stock'
  },
  manufacturer: {
    '@type': 'Organization',
    name: product.manufacturer || 'Promocionales en Stock'
  },
  offers: {
    '@type': 'Offer',
    url: `${SITE_URL}/producto/${product.slug || product.id}`,
    // El precio no se muestra públicamente en el sitio, así que no se declara aquí
    // (Google penaliza datos estructurados que no coinciden con lo visible en la página).
    ...(product.price ? {
      price: product.price,
      priceCurrency: 'COP',
      priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    } : {}),
    availability: product.stock > 0
      ? 'https://schema.org/InStock' 
      : 'https://schema.org/OutOfStock',
    seller: {
      '@id': `${SITE_URL}/#organization`
    },
    shippingDetails: {
      '@type': 'OfferShippingDetails',
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: '0',
        currency: 'COP'
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: 'CO'
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 3,
          unitCode: 'DAY'
        },
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 5,
          unitCode: 'DAY'
        }
      }
    }
  },
  category: product.category || 'Productos Promocionales',
  additionalProperty: [
    {
      '@type': 'PropertyValue',
      name: 'Personalizable',
      value: 'Sí'
    },
    {
      '@type': 'PropertyValue',
      name: 'Cantidad mínima',
      value: product.minQuantity || '50 unidades'
    }
  ],
  aggregateRating: product.rating ? {
    '@type': 'AggregateRating',
    ratingValue: product.rating.average || 4.5,
    reviewCount: product.rating.count || 10
  } : undefined
});

export const generateFullProductPageSchema = ({
  products,
  currentPage,
  totalCount,
  pageSize = 12,
  pageUrl,
  pageTitle,
  pageDescription,
  breadcrumbs
}) => ({
  '@context': 'https://schema.org',
  '@graph': [
    generateWebsiteSchema(),
    generateOrganizationSchema(),
    generateBreadcrumbSchema(breadcrumbs),
    {
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}${pageUrl}`,
      url: `${SITE_URL}${pageUrl}`,
      name: pageTitle,
      description: pageDescription,
      isPartOf: {
        '@id': `${SITE_URL}/#website`
      },
      breadcrumb: {
        '@id': `${SITE_URL}${pageUrl}#breadcrumb`
      },
      inLanguage: 'es-CO',
      potentialAction: [{
        '@type': 'ReadAction',
        target: [`${SITE_URL}${pageUrl}`]
      }]
    },
    generateProductListSchema({
      products,
      currentPage,
      totalCount,
      pageSize,
      pageUrl
    })
  ]
});